/**
 * KeviaFollowupPanel — FASE 22
 *
 * Collapsible panel listing followup items and active workflows.
 * Read-only summary — no mutations from this component.
 */

import { useState } from "react";
import { ChevronDown, ChevronUp, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import { computeFollowupItems } from "../../lib/kevia-workflows/followupEngine";
import { getPriorityWorkflows }  from "../../lib/kevia-workflows/workflowPriorities";
import { getWorkflowDefinition } from "../../lib/kevia-workflows/workflowSchemas";

const KIND_ICON = {
  overdue:     <AlertTriangle size={12} className="text-red-500 shrink-0" />,
  near_limit:  <Clock size={12} className="text-amber-500 shrink-0" />,
  stagnant:    <Clock size={12} className="text-slate-400 shrink-0" />,
  no_progress: <Clock size={12} className="text-slate-400 shrink-0" />,
};

export default function KeviaFollowupPanel({ onWorkflowSelect }) {
  const [open, setOpen] = useState(false);

  const followupItems = computeFollowupItems();
  const topWorkflows  = getPriorityWorkflows(3);

  const hasContent = followupItems.length > 0 || topWorkflows.length > 0;
  if (!hasContent) return null;

  const urgentCount = followupItems.filter((i) => i.kind === "overdue").length;

  return (
    <div className="rounded-[16px] border border-slate-200/60 dark:border-slate-700/50 bg-white dark:bg-slate-800 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
      {/* Toggle header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full px-3.5 py-2.5
                   hover:bg-slate-50 dark:hover:bg-slate-700/50 active:bg-slate-100 dark:active:bg-slate-700/60 transition-colors duration-150"
      >
        <div className="flex items-center gap-2">
          {urgentCount > 0
            ? <AlertTriangle size={13} className="text-red-500" />
            : <Clock size={13} className="text-blue-500" />}
          <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
            Seguimientos activos
          </span>
          {urgentCount > 0 && (
            <span className="text-[10px] font-semibold text-white bg-red-500 rounded-full px-1.5 py-0.5">
              {urgentCount}
            </span>
          )}
        </div>
        {open ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
      </button>

      {/* Body */}
      {open && (
        <div className="border-t border-slate-100 dark:border-slate-700/60 px-3.5 py-3 space-y-3">
          {/* Followup items */}
          {followupItems.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Necesitan atención
              </p>
              {followupItems.slice(0, 4).map((item) => (
                <div
                  key={item.workflowId}
                  onClick={() => onWorkflowSelect?.(item.workflowId)}
                  className="flex items-start gap-2 cursor-pointer group"
                >
                  <div className="mt-0.5">{KIND_ICON[item.kind] ?? KIND_ICON.stagnant}</div>
                  <p className="text-[11px] text-slate-600 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200 leading-snug transition-colors duration-150">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Top workflows */}
          {topWorkflows.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Procesos activos
              </p>
              {topWorkflows.map((wf) => {
                const def        = getWorkflowDefinition(wf.type);
                const entityName = wf.related_entities?.[0]?.name;
                const pct        = Math.round((wf.progress ?? 0) * 100);
                return (
                  <div
                    key={wf.id}
                    onClick={() => onWorkflowSelect?.(wf.id)}
                    className="flex items-center gap-2 cursor-pointer group"
                  >
                    <CheckCircle2 size={12} className="text-blue-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-slate-700 dark:text-slate-300 truncate group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors duration-150">
                        {def?.label ?? wf.type}
                        {entityName ? ` — ${entityName}` : ""}
                      </p>
                    </div>
                    <span className="text-[10px] text-slate-400 shrink-0">{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
